import { GridBaseDataProvider } from '../../../../../../../../grid/GridBaseDataProvider';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
} from '../../../../../../../../grid/types/common';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../configs/page-config';
import _ from 'lodash';
import { DefaultPolicyColumnKeys } from '../configs/column-config';
import { filterRows } from '../../../../../../../../grid/utils/filters';

export abstract class GzPolicyUiSettingsGridDataProvider<
  Column extends number,
  ColumnKeys extends string,
  GridItem extends object
> extends GridBaseDataProvider<Column, ColumnKeys> {
  private cachedGridItemsResponse: GridItem[];

  public constructor(private gridItems: Array<GridItem>) {
    super();
    this.buildPosition();
  }

  get items(): Array<GridItem> {
    return this.gridItems.map(item => {
      // remove defaultPositionInGrid from the item
      const { defaultPositionInGrid, ...rest } = item as any;
      return rest;
    });
  }

  set items(value: Array<GridItem>) {
    this.gridItems = value;
    this.buildPosition();
  }

  addItem(item: GridItem, position?: number) {
    // set default position if not provided
    const itemPosition = position ?? this.gridItems.length + 1;
    _.set(item, [DefaultPolicyColumnKeys.POSITION], itemPosition);

    // add item to the end of the list if position is not provided
    if (position === undefined) {
      this.gridItems.push(item);
      return;
    }

    // add item to the specified position
    this.gridItems.splice(position - 1, 0, item);
    this.buildPosition();
  }

  removeItems(positions: number[]) {
    // sort positions in descending order
    positions.sort((a, b) => b - a);

    // remove items from the list
    for (const position of positions) {
      this.gridItems.splice(position - 1, 1);
    }

    this.buildPosition();
  }

  updateItem(position: number, updatedItem: GridItem) {
    // update item in the list by adding defaultPositionInGrid if not provided
    if (updatedItem[DefaultPolicyColumnKeys.POSITION] === undefined) {
      this.gridItems[position - 1] = {
        ...updatedItem,
        [DefaultPolicyColumnKeys.POSITION]: position,
      };
      return;
    }

    // update item in the list
    this.gridItems[position - 1] = updatedItem;
  }

  moveItem(oldPosition: number, newPosition: number) {
    if (oldPosition === newPosition) {
      return;
    }

    // move item in the list
    const item = this.gridItems[oldPosition - 1];
    this.gridItems.splice(oldPosition - 1, 1);
    this.gridItems.splice(newPosition - 1, 0, item);

    this.buildPosition();
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    // page number is set to 0 if previous request had no data (see GridViewController::fetchAndOverwriteRowsInGrid)
    // in this case we need to use the default page number
    const currentPageNumber = request.pageNumber ? request.pageNumber : DEFAULT_PAGE_NUMBER;
    const currentPageSize = request.pageSize ?? DEFAULT_PAGE_SIZE;
    const start = (currentPageNumber - 1) * currentPageSize;
    const end = start + currentPageSize;
    const sortColumnName = request.sortColumnName ?? null;
    const sortDirection = request.sortDirection ?? null;

    // filter items
    this.cachedGridItemsResponse = filterRows(
      request.filters,
      this.getItemsWithAdditionalFrontendFields(this.gridItems),
      false
    );

    // sort items
    if (sortColumnName && sortDirection) {
      this.cachedGridItemsResponse.sort((a, b) => {
        return this.sortByColumnMethod(a, b, sortColumnName, sortDirection);
      });
    }

    // keep only items for the current page
    const numItems = this.cachedGridItemsResponse.length;
    this.cachedGridItemsResponse = this.cachedGridItemsResponse.slice(start, end);

    return {
      summary: '',
      numItems: numItems,
    };
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<{ rawEvents: Array<GridItem> }> {
    if (!this.cachedGridItemsResponse) {
      console.error('no cached response');
    }

    return {
      rawEvents: this.cachedGridItemsResponse,
    };
  }

  async updateSettings(request: UpdateSettingsRequest<Column>): Promise<{}> {
    return {};
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    return;
  }

  /**
   * This method should be implemented in the child class if sorting is available.
   * @param a
   * @param b
   * @param sortColumnName
   * @param sortDirection
   */
  sortByColumnMethod(a: GridItem, b: GridItem, sortColumnName: string, sortDirection: string): number {
    return 0;
  }

  /**
   * This method adds a position to each item in the grid.
   * @private
   */
  private buildPosition() {
    this.gridItems.forEach((item, index) => {
      item[DefaultPolicyColumnKeys.POSITION] = index + 1;
    });
  }

  /**
   * This method should be implemented in the child class to convert items to the frontend format if needed.
   * This method is useful when the items are stored in a complex object in the backend.
   *
   * IMPORTANT!
   *  - Keep backend fields for simplicity. Add frontend fields in the new object.
   *  - Be aware to use map instead of forEach to return a new array.
   *  - Also, be aware that the items should be converted back to the backend format in the updateItem method.
   *    Use sanitizeGridItemBeforeEdit method for that in web component class.
   *
   * @param items
   */
  protected getItemsWithAdditionalFrontendFields(items: GridItem[]): any[] {
    return this.gridItems;
  }
}
